import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  CheckCircle, Clock, Building2, Euro, FileText, Download,
  AlertCircle, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const StudentFundingPage = ({ onMenuClick }) => {
  const [loading, setLoading] = useState(true);
  const [fundingData, setFundingData] = useState(null);

  useEffect(() => {
    loadFundingStatus();
  }, []);

  const loadFundingStatus = async () => {
    try {
      const response = await api.get('/finance/student/funding-status');
      setFundingData(response.data);
    } catch (error) {
      console.error('Error loading funding status:', error);
      toast.error('Erreur lors du chargement');
    }
    setLoading(false);
  };

  const getStatusIcon = (status) => {
    if (status === 'Prise en charge confirmée') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
    return <Clock className="w-5 h-5 text-yellow-500" />;
  };

  const getTypeIcon = (type) => {
    if (type === 'opco' || type === 'company') {
      return <Building2 className="w-5 h-5 text-blue-500" />;
    }
    return <Euro className="w-5 h-5 text-green-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="glass-header sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4 border-b border-slate-200 dark:border-slate-700">
        <h1 className="text-2xl md:text-3xl font-bold text-indigo-950 dark:text-white font-outfit">
          Mon financement
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
          Statut de prise en charge de votre formation
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!fundingData?.fundings || fundingData.fundings.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
            <Shield className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Aucun financement configuré
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Votre organisme de formation n'a pas encore configuré les informations de financement pour votre formation.
              Contactez votre administration pour plus d'informations.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {fundingData.fundings.map((funding) => (
              <div
                key={funding.id}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
                data-testid="student-funding-card"
              >
                {/* Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                        Financement de votre formation
                      </h2>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Coût total: {funding.total_amount?.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      funding.funding_status === 'confirmed' || funding.funding_status === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}>
                      {funding.funding_status === 'confirmed' || funding.funding_status === 'active' ? 'Confirmé' : 'En cours'}
                    </div>
                  </div>
                </div>

                {/* Funding Sources */}
                <div className="p-6">
                  <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">
                    Sources de financement
                  </h3>
                  
                  <div className="space-y-3">
                    {funding.funding_sources.map((source, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                        data-testid={`funding-source-${source.type}`}
                      >
                        <div className="flex items-center gap-3">
                          {getTypeIcon(source.type)}
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {source.name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {source.type === 'opco' ? 'OPCO' : source.type === 'company' ? 'Entreprise' : 'Financement personnel'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-semibold text-slate-900 dark:text-white">
                            {source.percentage?.toFixed(0)}%
                          </span>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(source.status)}
                            <span className={`text-sm ${
                              source.status === 'Prise en charge confirmée'
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-yellow-600 dark:text-yellow-400'
                            }`}>
                              {source.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Info message for third-party funding */}
                  {funding.funding_sources.some(s => s.type === 'opco' || s.type === 'company') && (
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex gap-3">
                        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                            Formation prise en charge
                          </p>
                          <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                            Votre formation est financée par un organisme tiers. 
                            Les factures sont directement envoyées à{' '}
                            <strong>
                              {funding.funding_sources.find(s => s.type === 'opco' || s.type === 'company')?.name}
                            </strong>.
                            Vous n'avez aucune démarche administrative à effectuer.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Student's own invoices (only if student pays part) */}
                  {funding.my_invoices && funding.my_invoices.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">
                        Mes factures
                      </h3>
                      
                      <div className="space-y-2">
                        {funding.my_invoices.map((inv) => (
                          <div
                            key={inv.id}
                            className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">
                                  {inv.invoice_number}
                                </p>
                                <p className="text-sm text-slate-500">
                                  Échéance: {inv.due_date}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold text-slate-900 dark:text-white">
                                  {inv.total_ttc?.toLocaleString('fr-FR')} €
                                </p>
                                <p className="text-sm text-slate-500">
                                  Payé: {inv.paid_amount?.toLocaleString('fr-FR')} €
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                inv.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-700'
                                  : inv.payment_status === 'overdue'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {inv.payment_status === 'paid' ? 'Payé' : 
                                 inv.payment_status === 'overdue' ? 'En retard' : 'En attente'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentFundingPage;
